import { InfoOutlined, LockOutlined } from "@mui/icons-material";
import { Box, Button, ButtonBase, Card, Typography } from "@mui/material";
import { ReactNode, useContext } from "react";
import { AppContext } from "../../context/AppContext";
import { formatCmpctNumber } from "../../helpers/utils";
import { HomeownerMortgage } from "../../interfaces/AppDefinitions";
import { INK, INK_2, LINE, MUTED, UP, cardSx, labelSx, withAlpha } from "../modern/shared";
import { useMortgage } from "./MortgageContext";
import MortgageForm from "./MortgageForm";

// Equity panel (Mortgage Insights). Before the homeowner adds their mortgage it says something true we
// already know (the gain since they bought) and offers the form; after, it shows their equity, what they
// could borrow and when they renew. variant picks the card style of the report layout it sits in.

const AMBER = { fg: "#8a5a00", bg: "#fff4d6" };
const BORROW_LIMIT = 0.8; // the usual refinance limit in Canada

function monthName(yyyyMm: string | null, style: "long" | "short" = "long") {
	if (!yyyyMm) return null;
	const d = new Date(`${yyyyMm}-01T00:00:00Z`);
	return d.toLocaleDateString("en-US", { month: style, year: "numeric", timeZone: "UTC" });
}

function Shell(props: { variant: "modern" | "classic"; children: ReactNode }) {
	if (props.variant === "classic") {
		return <Card sx={{ p: { xs: "18px", md: "22px 26px" }, textAlign: "left", color: INK }}>{props.children}</Card>;
	}
	return <Box sx={{ ...cardSx, p: { xs: "18px", md: "22px 26px" } }}>{props.children}</Box>;
}

export default function EquityPanel(props: { variant: "modern" | "classic"; estimatedValue: number }) {
	const ui = useMortgage();
	const { appState } = useContext(AppContext);
	if (!ui?.enabled || !ui.address) return null;
	const agent = appState.info.agent?.agentName || "your agent";
	const inlineForm = ui.embedded && ui.form !== null;

	return (
		<Shell variant={props.variant}>
			{inlineForm ? (
				<MortgageForm
					compact
					initial={ui.mortgage}
					agentName={appState.info.agent?.agentName}
					cancelLabel={ui.form?.prompted ? "Not now" : "Cancel"}
					onCancel={ui.closeForm}
					onSubmit={ui.save}
				/>
			) : ui.mortgage ? (
				<EquityView mortgage={ui.mortgage} value={props.estimatedValue} agent={agent} />
			) : (
				<Teaser value={props.estimatedValue} agent={agent} />
			)}
		</Shell>
	);
}

function Teaser(props: { value: number; agent: string }) {
	const ui = useMortgage()!;
	const { appState } = useContext(AppContext);
	const brand = appState.colorPallete?.["scheme1"] || "#1f5fae";
	const { purchaseYear, purchasePrice } = ui.address ?? { purchaseYear: null, purchasePrice: null };
	const gain = purchasePrice ? props.value - purchasePrice : 0;
	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
			<Typography sx={labelSx}>Your equity</Typography>
			<Typography sx={{ fontSize: { xs: "18px", md: "20px" }, fontWeight: 750, lineHeight: 1.3, color: INK }}>
				{gain > 0 && purchaseYear ? (
					<>
						Your home is worth{" "}
						<Box component="span" sx={{ color: UP.fg }}>
							{formatCmpctNumber(gain)} more
						</Box>{" "}
						than you paid in {purchaseYear}.
					</>
				) : (
					"See how much of your home you own."
				)}
			</Typography>
			<Box
				aria-hidden="true"
				sx={{
					height: 14,
					borderRadius: 99,
					background: "repeating-linear-gradient(90deg, #e3e8ef 0 18px, #edf0f5 18px 36px)",
				}}
			/>
			<Typography sx={{ fontSize: "13px", color: MUTED }}>
				Add your mortgage to see how much of it you own, what you could borrow, and when to renew.
			</Typography>
			<Box>
				<Button
					variant="contained"
					disableElevation
					onClick={ui.openForm}
					sx={{
						backgroundColor: brand,
						textTransform: "none",
						fontWeight: 700,
						borderRadius: "10px",
						px: "18px",
						"&:hover": { backgroundColor: brand, filter: "brightness(1.08)" },
					}}
				>
					Add my mortgage · 30 sec
				</Button>
			</Box>
			<Typography sx={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: MUTED }}>
				<LockOutlined sx={{ fontSize: 14 }} />
				Private. Shared only with {props.agent}.
			</Typography>
		</Box>
	);
}

function EquityView(props: { mortgage: HomeownerMortgage; value: number; agent: string }) {
	const ui = useMortgage()!;
	const { appState } = useContext(AppContext);
	const brand = appState.colorPallete?.["scheme1"] || "#1f5fae";
	const m = props.mortgage;
	const value = props.value || 0;
	const balance = m.balanceToday;
	const equity = value - balance;
	const share = value > 0 ? Math.max(0, Math.min(1, equity / value)) : 0;
	const borrowable = Math.max(0, value * BORROW_LIMIT - balance);
	const told = new Date(m.updatedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" });
	const renews = monthName(m.termEnd, "short");

	let note: ReactNode = null;
	if (m.termEnded) {
		note = (
			<>
				<b>Your term ended in {monthName(m.termEnd)}.</b> Update your details so your equity stays accurate.
			</>
		);
	} else if (m.renewalWindowOpen) {
		note = (
			<>
				<b>Your renewal window is open.</b> Most lenders let you lock in a new rate up to 120 days before your
				term ends. {props.agent} can help you compare.
			</>
		);
	} else if (m.stale) {
		note = <>It's been a year since you added these details. Update them to keep your equity accurate.</>;
	}

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "baseline",
					flexWrap: "wrap",
					gap: "8px",
				}}
			>
				<Typography sx={labelSx}>Your equity</Typography>
				<Typography sx={{ fontSize: "12px", color: MUTED }}>
					{ui.justSaved !== null && ui.justSaved === appState.currentAddrId ? "Saved. " : ""}From what you
					told us {told} ·{" "}
					<ButtonBase
						onClick={ui.openForm}
						sx={{ fontSize: "12px", color: INK_2, textDecoration: "underline", verticalAlign: "baseline" }}
					>
						Edit
					</ButtonBase>
				</Typography>
			</Box>
			<Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
				<Typography
					component="p"
					sx={{
						fontSize: { xs: "34px", md: "40px" },
						fontWeight: 800,
						letterSpacing: "-.035em",
						lineHeight: 1.05,
						fontVariantNumeric: "tabular-nums",
						color: INK,
					}}
				>
					{equity < 0 ? `-${formatCmpctNumber(-equity)}` : formatCmpctNumber(equity)}
				</Typography>
				<Box
					sx={{
						fontSize: "12px",
						fontWeight: 700,
						px: "10px",
						py: "3px",
						borderRadius: "99px",
						backgroundColor: withAlpha(brand, 0.1),
						color: brand,
					}}
				>
					{equity < 0 ? "More than the estimate is owed" : `${Math.round(share * 100)}% of your home`}
				</Box>
			</Box>
			<Box>
				<Box
					aria-hidden="true"
					sx={{
						display: "flex",
						height: 14,
						borderRadius: 99,
						overflow: "hidden",
						backgroundColor: "#e9edf3",
					}}
				>
					<Box sx={{ width: `${share * 100}%`, backgroundColor: brand }} />
					<Box sx={{ flex: 1, backgroundColor: "#c8d0dc" }} />
				</Box>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						gap: "10px",
						mt: "6px",
						fontSize: "12px",
						color: MUTED,
						flexWrap: "wrap",
					}}
				>
					<span>
						<Swatch color={brand} />
						Equity <b style={{ color: INK }}>{formatCmpctNumber(Math.max(0, equity))}</b>
					</span>
					<span>
						<Swatch color="#c8d0dc" />
						Mortgage <b style={{ color: INK }}>{formatCmpctNumber(balance)}</b>
					</span>
				</Box>
			</Box>
			{note && (
				<Box
					sx={{
						display: "flex",
						gap: "10px",
						alignItems: "flex-start",
						backgroundColor: AMBER.bg,
						color: AMBER.fg,
						borderRadius: "12px",
						p: "10px 14px",
						fontSize: "13px",
						fontWeight: 500,
					}}
				>
					<InfoOutlined sx={{ fontSize: 17, mt: "1px" }} />
					<span>{note}</span>
				</Box>
			)}
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(3, minmax(0, 1fr))" },
					borderTop: `1px solid ${LINE}`,
					mx: { xs: "-18px", md: "-26px" },
					mb: { xs: "-18px", md: "-22px" },
				}}
			>
				<Stat label="Equity you can borrow" value={formatCmpctNumber(borrowable)} hint="up to 80% of value" />
				<Stat
					label="Monthly payment"
					value={formatCmpctNumber(m.payment)}
					hint={`${m.rate}% ${m.rateType}${m.paymentEntered ? "" : " · estimated"}`}
				/>
				<Stat
					label="Renews"
					value={renews ?? "Not sure"}
					hint={
						m.termEnded
							? "term ended"
							: m.daysToRenewal !== null
								? `in ${m.daysToRenewal} day${m.daysToRenewal === 1 ? "" : "s"}`
								: (m.lender ?? "")
					}
				/>
			</Box>
		</Box>
	);
}

function Swatch(props: { color: string }) {
	return (
		<Box
			component="i"
			sx={{
				display: "inline-block",
				width: 9,
				height: 9,
				borderRadius: "3px",
				mr: "5px",
				backgroundColor: props.color,
			}}
		/>
	);
}

function Stat(props: { label: string; value: string; hint: string }) {
	return (
		<Box
			sx={{
				p: { xs: "12px 18px", md: "14px 26px 18px" },
				borderTop: { xs: `1px solid ${LINE}`, sm: "none" },
				borderLeft: { xs: "none", sm: `1px solid ${LINE}` },
				"&:first-of-type": { borderTop: "none", borderLeft: "none" },
				minWidth: 0,
			}}
		>
			<Typography sx={{ fontSize: "12px", color: MUTED }}>{props.label}</Typography>
			<Typography sx={{ fontSize: "19px", fontWeight: 700, color: INK, fontVariantNumeric: "tabular-nums" }}>
				{props.value}
			</Typography>
			<Typography sx={{ fontSize: "12px", color: MUTED }}>{props.hint}</Typography>
		</Box>
	);
}
